import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import firebase from '@config/firebase.config';
import PulseLoader from 'react-spinners/PulseLoader';
import { regularizationActionTypes } from '@store/Regularization/Regularization.actionTypes';
import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { useTeamMemberDetailsForSelect as useTeamMemberPicker } from '@hooks/useTeamMemberDetailsForSelect';
import { Table } from '@/components/Table';
import { columnConfig } from './Regularization.config';
import styles from './Regularization.module.scss';
import { Modal } from '@/components/Modal';
import { RegularizationModal } from './RegularizationModal.component';
import { UpdateRegularizationModal } from './UpdateRegularizationModal.component';
import { Button, buttonVariants } from '@/components/Button';
const database = firebase.firestore();

export const RegularizationComponent = () => {
  // ==================== Redux Selectors ====================
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { profile } = useSelector((state) => state.auth);
  const teams = useSelector((state) => ({
    projects: state.team.projects,
    active: state.team.active,
    teams: state?.team?.teams,
  }));
  const regularizationState = useSelector(
    (state) => state.regularization || {}
  );
  const dispatch = useDispatch();

  const activeTeamId = teams.active?.activeTeamid ?? null;
  const { memberDetails } = useTeamMemberPicker(activeTeamId);

  // ==================== Computed Values ====================
  const teamProjects = useMemo(
    () =>
      Array.isArray(teams.projects)
        ? teams.projects.filter((item) => !item.data?.deleted)
        : [],
    [teams.projects]
  );

  const [loading, setLoading] = useState(false);
  const [regList, setRegList] = useState([]);

  // Modal states
  const [showAddRequestModal, setShowAddRequestModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isAddModalBusy, setIsAddModalBusy] = useState(false);
  const [isUpdateModalBusy, setIsUpdateModalBusy] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const selectedRowData = useRef(null);

  // ==================== Data Fetching ====================
  const fetchRegulationRequests = () => {
    if (!activeCompany?.companyId || !profile?.id) return;
    setLoading(true);
    dispatch({
      type: regularizationActionTypes.GET_REQUESTS,
      payload: {
        userId: profile.id,
        companyId: activeCompany.companyId,
      },
    });
  };

  const fetchRegulationRequestsRef = React.useRef(fetchRegulationRequests);
  fetchRegulationRequestsRef.current = fetchRegulationRequests;

  // Update local state when Redux state changes
  useEffect(() => {
    if (regularizationState.requests) {
      setRegList(regularizationState.requests);
      setLoading(regularizationState.loading || false);
    }
  }, [regularizationState.requests, regularizationState.loading]);

  const fetchProjectTasks = useCallback(
    async (projectId, callback) => {
      if (!projectId || !profile?.id) {
        return;
      }

      try {
        const snapshot = await database
          .collection('taskDatabase')
          .where('ProjectID', '==', projectId)
          .get();

        const tasks = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((task) => task.Assignee === profile.id && !task.Deleted);
        if (callback) {
          callback(tasks);
        }
      } catch (error) {
        console.error('Error fetching project tasks:', error);
      }
    },
    [profile?.id]
  );

  const handleRowClick = useCallback(
    (rowData) => {
      if (rowData?.status === 'Declined') return;
      selectedRowData.current = rowData;

      setSelectedRequest(rowData);
      setIsUpdateModalBusy(false);
      if (rowData.projectId) {
        fetchProjectTasks(rowData.projectId);
      }
      setShowUpdateModal(true);
    },
    [fetchProjectTasks]
  );

  // ==================== Table Templates ====================

  // ==================== Effects ====================
  // Load projects and regulation list only when active team or company changes (not on project select)
  useEffect(() => {
    if (activeTeamId && activeCompany?.companyId) {
      dispatch({
        type: teamActionTypes.GET_PROJECT,
        payload: { companyId: activeCompany.companyId },
      });
      fetchRegulationRequestsRef.current();
    }
    // Intentionally omit fetchRegulationRequests so this only runs when team/company id changes
  }, [activeTeamId, activeCompany?.companyId, dispatch]);
  return (
    <>
      <div className={styles.regularizationPage}>
        <Button
          text="+ Add Request"
          variant={buttonVariants.rounded}
          onClick={() => {
            setIsAddModalBusy(false);
            setShowAddRequestModal(true);
          }}
        />
        <Modal
          isVisible={showAddRequestModal}
          preventClose={isAddModalBusy}
          onClose={() => {
            if (isAddModalBusy) return;
            setShowAddRequestModal(false);
          }}
        >
          <RegularizationModal
            teams={teams}
            fetchProjectTasks={fetchProjectTasks}
            onBusyChange={setIsAddModalBusy}
            onClose={() => {
              setIsAddModalBusy(false);
              setShowAddRequestModal(false);
            }}
          />
        </Modal>
        <Modal
          isVisible={showUpdateModal}
          preventClose={isUpdateModalBusy}
          onClose={() => {
            if (isUpdateModalBusy) return;
            setShowUpdateModal(false);
          }}
        >
          <UpdateRegularizationModal
            rowData={selectedRequest}
            teamProjects={teamProjects}
            teams={teams}
            fetchProjectTasks={fetchProjectTasks}
            fetchRegulationRequests={fetchRegulationRequests}
            onBusyChange={setIsUpdateModalBusy}
            onClose={() => {
              setIsUpdateModalBusy(false);
              setShowUpdateModal(false);
            }}
          />
        </Modal>
        {loading ? (
          <div className="w-100 d-flex justify-content-center">
            <PulseLoader color="#4f58bb" />
          </div>
        ) : (
          <div className={styles.regularizationTableContainer}>
            <Table
              onRowClick={handleRowClick}
              data={regList}
              columns={columnConfig({
                teamProjects,
                memberDetails,
              })}
            />
          </div>
        )}
      </div>
    </>
  );
};
